/* @jsx h */
import { defineComponent, h, ref } from "vue";
import type { ProductNutritionalInformation } from "@lemmonhq/types";

export interface NutritionFormProps {
  isOpen: boolean;
  nutritionData: ProductNutritionalInformation;
  onSave: (nutrition: ProductNutritionalInformation) => void;
  onClose: () => void;
}

export const NutritionForm = defineComponent<NutritionFormProps>({
  name: "NutritionForm",
  props: ["isOpen", "nutritionData", "onSave", "onClose"],
  setup(props) {
    const formData = ref<ProductNutritionalInformation>({
      ...props.nutritionData,
    });

    const addAllergen = (allergen: string) => {
      if (!formData.value.allergens) formData.value.allergens = [];
      if (!formData.value.allergens.includes(allergen)) {
        formData.value.allergens.push(allergen);
      }
    };

    const removeAllergen = (allergen: string) => {
      if (formData.value.allergens) {
        formData.value.allergens = formData.value.allergens.filter(
          (a) => a !== allergen,
        );
      }
    };

    const addDietaryTag = (tag: string) => {
      if (!formData.value.dietaryTags) formData.value.dietaryTags = [];
      if (!formData.value.dietaryTags.includes(tag)) {
        formData.value.dietaryTags.push(tag);
      }
    };

    const removeDietaryTag = (tag: string) => {
      if (formData.value.dietaryTags) {
        formData.value.dietaryTags = formData.value.dietaryTags.filter(
          (t) => t !== tag,
        );
      }
    };

    const addMicronutrient = (name: string, value: number) => {
      if (!formData.value.micronutrients) formData.value.micronutrients = {};
      formData.value.micronutrients[name] = value;
    };

    const commonAllergens = [
      "milk",
      "eggs",
      "fish",
      "shellfish",
      "nuts",
      "peanuts",
      "wheat",
      "gluten",
      "soy",
      "sesame",
      "sulfites",
    ];
    const commonDietaryTags = [
      "vegetarian",
      "vegan",
      "gluten-free",
      "dairy-free",
      "low-carb",
      "keto",
      "organic",
      "spicy",
    ];
    const commonMicronutrients = [
      "vitaminA",
      "vitaminC",
      "vitaminD",
      "calcium",
      "iron",
      "potassium",
    ];

    const handleSave = () => {
      props.onSave(formData.value);
    };

    return () => {
      if (!props.isOpen) return null;

      return (
        <div class="modal modal-open">
          <div class="modal-box max-w-6xl max-h-[90vh] overflow-y-auto">
            <h3 class="font-serif text-2xl font-light mb-6">
              Comprehensive Nutritional Information
            </h3>

            <div class="space-y-8">
              {/* Serving Size */}
              <div class="bg-gray-50 rounded-xl p-6">
                <h4 class="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span class="material-symbols-outlined text-blue-600">
                    straighten
                  </span>
                  Serving Size & Container Info
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">Amount</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.value.servingSize.amount}
                      onInput={(e) =>
                        (formData.value.servingSize.amount =
                          parseFloat((e.target as HTMLInputElement).value) || 0)
                      }
                      class="input input-bordered"
                    />
                  </div>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">Unit</span>
                    </label>
                    <select
                      value={formData.value.servingSize.unit}
                      onChange={(e) =>
                        (formData.value.servingSize.unit = (
                          e.target as HTMLSelectElement
                        ).value)
                      }
                      class="select select-bordered"
                    >
                      <option value="g">grams (g)</option>
                      <option value="ml">milliliters (ml)</option>
                      <option value="pieces">pieces</option>
                      <option value="slices">slices</option>
                      <option value="cups">cups</option>
                      <option value="oz">ounces (oz)</option>
                      <option value="tbsp">tablespoons</option>
                      <option value="tsp">teaspoons</option>
                    </select>
                  </div>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">
                        Servings per Container
                      </span>
                    </label>
                    <input
                      type="number"
                      value={
                        formData.value.servingSize.servingsPerContainer || ""
                      }
                      onInput={(e) =>
                        (formData.value.servingSize.servingsPerContainer =
                          parseInt((e.target as HTMLInputElement).value) ||
                          undefined)
                      }
                      class="input input-bordered"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>

              {/* Energy Content */}
              <div class="bg-orange-50 rounded-xl p-6">
                <h4 class="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span class="material-symbols-outlined text-orange-600">
                    local_fire_department
                  </span>
                  Energy Content
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">Calories</span>
                    </label>
                    <input
                      type="number"
                      value={formData.value.calories}
                      onInput={(e) =>
                        (formData.value.calories =
                          parseFloat((e.target as HTMLInputElement).value) || 0)
                      }
                      class="input input-bordered input-lg"
                      placeholder="250"
                    />
                  </div>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">
                        Energy (kJ) - Optional
                      </span>
                    </label>
                    <input
                      type="number"
                      value={formData.value.energyKj || ""}
                      onInput={(e) =>
                        (formData.value.energyKj =
                          parseFloat((e.target as HTMLInputElement).value) ||
                          undefined)
                      }
                      class="input input-bordered"
                      placeholder="1050"
                    />
                  </div>
                </div>
              </div>

              {/* Macronutrients */}
              <div class="bg-green-50 rounded-xl p-6">
                <h4 class="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span class="material-symbols-outlined text-green-600">
                    nutrition
                  </span>
                  Macronutrients
                </h4>

                {/* Primary Macros */}
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">Total Fat (g)</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.value.macronutrients.totalFat.value}
                      onInput={(e) =>
                        (formData.value.macronutrients.totalFat.value =
                          parseFloat((e.target as HTMLInputElement).value) || 0)
                      }
                      class="input input-bordered"
                    />
                  </div>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">
                        Total Carbohydrates (g)
                      </span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={
                        formData.value.macronutrients.totalCarbohydrate.value
                      }
                      onInput={(e) =>
                        (formData.value.macronutrients.totalCarbohydrate.value =
                          parseFloat((e.target as HTMLInputElement).value) || 0)
                      }
                      class="input input-bordered"
                    />
                  </div>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">Protein (g)</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.value.macronutrients.protein.value}
                      onInput={(e) =>
                        (formData.value.macronutrients.protein.value =
                          parseFloat((e.target as HTMLInputElement).value) || 0)
                      }
                      class="input input-bordered"
                    />
                  </div>
                </div>

                {/* Detailed Fat Breakdown */}
                <div class="bg-white rounded-lg p-4 mb-4">
                  <h5 class="font-medium mb-3">Fat Breakdown (Optional)</h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">
                          Saturated Fat (g)
                        </span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={
                          formData.value.macronutrients.totalFat.saturatedFat ||
                          ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          formData.value.macronutrients.totalFat.saturatedFat =
                            isNaN(val) ? undefined : val;
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">Trans Fat (g)</span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={
                          formData.value.macronutrients.totalFat.transFat || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          formData.value.macronutrients.totalFat.transFat =
                            isNaN(val) ? undefined : val;
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">
                          Monounsaturated (g)
                        </span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={
                          formData.value.macronutrients.totalFat
                            .monounsaturatedFat || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          formData.value.macronutrients.totalFat.monounsaturatedFat =
                            isNaN(val) ? undefined : val;
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">
                          Polyunsaturated (g)
                        </span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={
                          formData.value.macronutrients.totalFat
                            .polyunsaturatedFat || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          formData.value.macronutrients.totalFat.polyunsaturatedFat =
                            isNaN(val) ? undefined : val;
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Carbohydrate Breakdown */}
                <div class="bg-white rounded-lg p-4 mb-4">
                  <h5 class="font-medium mb-3">
                    Carbohydrate Breakdown (Optional)
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">
                          Dietary Fiber (g)
                        </span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={
                          formData.value.macronutrients.totalCarbohydrate
                            .dietaryFiber || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          formData.value.macronutrients.totalCarbohydrate.dietaryFiber =
                            isNaN(val) ? undefined : val;
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">Total Sugars (g)</span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={
                          formData.value.macronutrients.totalCarbohydrate
                            .totalSugars || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          formData.value.macronutrients.totalCarbohydrate.totalSugars =
                            isNaN(val) ? undefined : val;
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">Added Sugars (g)</span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={
                          formData.value.macronutrients.totalCarbohydrate
                            .addedSugars || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          formData.value.macronutrients.totalCarbohydrate.addedSugars =
                            isNaN(val) ? undefined : val;
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Other Macronutrients */}
                <div class="bg-white rounded-lg p-4">
                  <h5 class="font-medium mb-3">
                    Additional Nutrients (Optional)
                  </h5>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">Cholesterol (mg)</span>
                      </label>
                      <input
                        type="number"
                        value={
                          formData.value.macronutrients.cholesterol?.value || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          if (isNaN(val)) {
                            formData.value.macronutrients.cholesterol =
                              undefined;
                          } else {
                            formData.value.macronutrients.cholesterol = {
                              value: val,
                              unit: "mg",
                            };
                          }
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                    <div class="form-control">
                      <label class="label">
                        <span class="label-text text-sm">Sodium (mg)</span>
                      </label>
                      <input
                        type="number"
                        value={
                          formData.value.macronutrients.sodium?.value || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          if (isNaN(val)) {
                            formData.value.macronutrients.sodium = undefined;
                          } else {
                            formData.value.macronutrients.sodium = {
                              value: val,
                              unit: "mg",
                            };
                          }
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Micronutrients */}
              <div class="bg-purple-50 rounded-xl p-6">
                <h4 class="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span class="material-symbols-outlined text-purple-600">
                    science
                  </span>
                  Vitamins & Minerals (Optional)
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {commonMicronutrients.map((nutrient) => (
                    <div key={nutrient} class="form-control">
                      <label class="label">
                        <span class="label-text text-sm capitalize">
                          {nutrient.replace(/([A-Z])/g, " $1").trim()}
                          {nutrient.includes("vitamin") ? " (µg)" : " (mg)"}
                        </span>
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={
                          (formData.value.micronutrients?.[
                            nutrient
                          ] as number) || ""
                        }
                        onInput={(e) => {
                          const val = parseFloat(
                            (e.target as HTMLInputElement).value,
                          );
                          if (!formData.value.micronutrients)
                            formData.value.micronutrients = {};
                          if (isNaN(val)) {
                            delete formData.value.micronutrients[nutrient];
                          } else {
                            formData.value.micronutrients[nutrient] = val;
                          }
                        }}
                        class="input input-bordered input-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Allergens */}
              <div class="bg-red-50 rounded-xl p-6">
                <h4 class="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span class="material-symbols-outlined text-red-600">
                    warning
                  </span>
                  Allergen Information
                </h4>
                <div class="flex flex-wrap gap-3 mb-4">
                  {commonAllergens.map((allergen) => (
                    <button
                      key={allergen}
                      onClick={() => {
                        if (formData.value.allergens?.includes(allergen)) {
                          removeAllergen(allergen);
                        } else {
                          addAllergen(allergen);
                        }
                      }}
                      class={`btn btn-sm transition-all ${
                        formData.value.allergens?.includes(allergen)
                          ? "btn-error"
                          : "btn-outline"
                      }`}
                    >
                      {allergen}
                    </button>
                  ))}
                </div>
                <div class="text-sm text-gray-600 bg-white p-3 rounded">
                  <strong>Selected allergens:</strong>{" "}
                  {formData.value.allergens?.join(", ") || "None"}
                </div>
              </div>

              {/* Dietary Tags */}
              <div class="bg-blue-50 rounded-xl p-6">
                <h4 class="font-semibold text-lg mb-4 flex items-center gap-2">
                  <span class="material-symbols-outlined text-blue-600">
                    eco
                  </span>
                  Dietary Preferences & Certifications
                </h4>
                <div class="flex flex-wrap gap-3 mb-4">
                  {commonDietaryTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => {
                        if (formData.value.dietaryTags?.includes(tag)) {
                          removeDietaryTag(tag);
                        } else {
                          addDietaryTag(tag);
                        }
                      }}
                      class={`btn btn-sm transition-all ${
                        formData.value.dietaryTags?.includes(tag)
                          ? "btn-success"
                          : "btn-outline"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <div class="text-sm text-gray-600 bg-white p-3 rounded">
                  <strong>Dietary tags:</strong>{" "}
                  {formData.value.dietaryTags?.join(", ") || "None"}
                </div>
              </div>

              {/* Notes */}
              <div class="bg-gray-50 rounded-xl p-6">
                <h4 class="font-semibold text-lg mb-4">Additional Notes</h4>
                <div class="form-control">
                  <textarea
                    value={formData.value.notes || ""}
                    onInput={(e) =>
                      (formData.value.notes =
                        (e.target as HTMLTextAreaElement).value || undefined)
                    }
                    class="textarea textarea-bordered"
                    rows={3}
                    placeholder="Any additional nutritional information, preparation notes, or special considerations..."
                  />
                </div>
              </div>
            </div>

            <div class="modal-action mt-8">
              <button onClick={props.onClose} class="btn btn-ghost">
                Cancel
              </button>
              <button
                onClick={handleSave}
                class="btn bg-gray-900 text-white hover:bg-gray-800"
              >
                Save Comprehensive Nutrition Data
              </button>
            </div>
          </div>
        </div>
      );
    };
  },
});
